import React from "react";

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error?: Error; resetError: () => void }>;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

class TileErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Tile component error:", error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return (
          <FallbackComponent
            error={this.state.error}
            resetError={this.resetError}
          />
        );
      }

      // Default fallback: render a simple error placeholder
      return (
        <pixiContainer>
          <pixiGraphics
            draw={(g: any) => {
              g.clear();
              g.setFillStyle({ color: 0xff0000 }); // Red background for error
              g.rect(0, 0, 180, 100); // Default tile size
              g.fill();
            }}
          />
        </pixiContainer>
      );
    }

    return this.props.children;
  }
}

// Default error fallback component
export function TileErrorFallback({
  error: _error,
  resetError: _resetError,
}: {
  error?: Error;
  resetError: () => void;
}) {
  return (
    <pixiContainer cullable={true}>
      <pixiGraphics
        draw={(g: any) => {
          g.clear();
          g.setFillStyle({ color: 0xff0000 }); // Red background for error
          g.rect(0, 0, 180, 100); // Default tile size
          g.fill();
        }}
      />
    </pixiContainer>
  );
}

export default TileErrorBoundary;
